import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { Upload, Download, Trash2, FileText, Check, ChevronDown, ChevronUp, ShieldCheck, Users, Circle } from 'lucide-react';
import * as SelfApi from '../selfApi';

const API = process.env.REACT_APP_API_URL || '';
// The brand blue of the speaker area's cards (SpeakerArea.js imports it for its own borders).
export const BLUE = '#49ABDB';

// "Your talk" panel inside a confirmed conference card. One talk, its files and the order they are
// presented in: the speaker's own state, the title suggestion, the co-speakers to coordinate with,
// the consent question, the numbered files with their presenters, the order's confirmation and
// "Add a file". Every write answers the talk row of GET /api/me/talks and the parent swaps it in.
const TalkArea = styled.div`margin-top:.7rem;padding-top:.7rem;border-top:1px solid rgba(15,23,42,.08);`;
// Collapsed-by-default disclosure header: keeps the card compact and shows a
// one-line summary of the talk (or an invite to add one) until expanded.
const ToggleRow = styled.button`display:flex;width:100%;justify-content:space-between;align-items:center;gap:.5rem;border:0;background:transparent;padding:0;cursor:pointer;text-align:left;color:#94a3b8;&:hover{color:#5B6B78;}`;
const ToggleLeft = styled.span`display:flex;flex-direction:column;gap:.15rem;min-width:0;`;
const ToggleTitle = styled.span`font-size:.68rem;font-weight:800;letter-spacing:.06em;text-transform:uppercase;color:#334155;`;
const ToggleSummary = styled.span`display:flex;gap:.5rem;align-items:center;min-width:0;font-size:.82rem;color:#111D4E;`;
const SummaryTitle = styled.span`overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-weight:600;`;
const SummarySlides = styled.span`display:inline-flex;gap:.25rem;align-items:center;min-width:0;font-size:.74rem;color:#5B6B78;`;
const SummaryFileName = styled.span`min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;`;
// What still waits for the speaker, on the collapsed header too: the card starts closed, and the order
// banner or a missing part must not hide behind the chevron.
const SummaryAttention = styled.span`align-self:flex-start;margin-top:.2rem;padding:.12rem .55rem;font-size:.72rem;font-weight:700;background:#fef3c7;color:#92400e;`;
const TalkBody = styled.div`margin-top:.5rem;`;
// A shared talk says so on the collapsed header too: the co-speakers are the first thing to know.
const SummaryShared = styled.span`display:inline-flex;gap:.3rem;align-items:center;min-width:0;font-size:.76rem;font-weight:600;color:#075985;`;

// A talk with co-speakers: one box at the top that says the files are shared and what the speakers
// settle together (who presents which file, the order), each with its state.
const SharedBox = styled.div`margin:.2rem 0 .6rem;padding:.8rem .85rem;background:#eff9fe;border-left:4px solid ${BLUE};`;
const SharedHead = styled.div`display:flex;align-items:center;gap:.45rem;font-weight:800;font-size:.9rem;color:#111D4E;`;
const SharedText = styled.p`margin:.35rem 0 .55rem;font-size:.8rem;line-height:1.45;color:#334155;`;
const TodoList = styled.ul`list-style:none;margin:.5rem 0 0;padding:0;display:flex;flex-direction:column;gap:.35rem;`;
const TodoItem = styled.li`display:flex;gap:.5rem;align-items:flex-start;font-size:.82rem;color:#111D4E;svg{flex:none;margin-top:.1rem;color:${p=>p.$done?'#16a34a':p.$idle?'#94a3b8':'#d97706'};}`;
const TodoText = styled.span`display:flex;flex-direction:column;gap:.05rem;min-width:0;`;
const TodoTitle = styled.span`font-weight:700;`;
const TodoDetail = styled.span`font-size:.74rem;color:#5B6B78;`;
// An answered block folds to one row (what was answered) and opens again on a click.
const FoldRow = styled.button`display:flex;width:100%;align-items:center;gap:.5rem;border:0;background:transparent;padding:0;margin:0;font:inherit;text-align:left;cursor:pointer;color:#111D4E;`;
const FoldTitle = styled.span`flex:1;min-width:0;display:flex;flex-wrap:wrap;align-items:center;gap:.35rem .55rem;font-size:.85rem;font-weight:700;`;
const FoldAction = styled.span`flex:none;display:inline-flex;align-items:center;gap:.2rem;font-size:.74rem;font-weight:700;color:#0369a1;`;
const FieldLabel = styled.label`display:block;font-size:.72rem;font-weight:700;color:#5B6B78;margin:.5rem 0 .25rem;`;
const TitleRow = styled.div`display:flex;gap:.4rem;align-items:stretch;`;
const TalkInput = styled.input`flex:1;min-width:0;padding:.5rem .55rem;border:1px solid #cbd5e1;font:inherit;font-size:.9rem;`;
const MiniButton = styled.button`display:inline-flex;align-items:center;gap:.3rem;border:0;padding:.5rem .7rem;background:${p=>p.$danger?'#dc2626':p.$ghost?'#eef2f7':BLUE};color:${p=>p.$ghost?'#334155':'#fff'};font-weight:700;font-size:.82rem;cursor:pointer;transition:background-color .3s;&:hover:not(:disabled){background:${p=>p.$danger?'#b91c1c':p.$ghost?'#e2e8f0':'#0056b3'};}&:disabled{opacity:.5;cursor:not-allowed;}`;
const SlideBtnRow = styled.div`display:flex;gap:.4rem;flex-wrap:wrap;align-items:center;`;
// The old slide name was an <a download> to a public uploads URL; a talk file is behind the bearer
// token now (/api/me/materials/{versionId}/file), so it is the button that runs that download.
const SlideName = styled.button`min-width:0;max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;background:none;border:0;padding:0;font:inherit;font-size:.88rem;font-weight:600;text-align:left;color:#0369a1;cursor:pointer;&:hover:not(:disabled){text-decoration:underline;}&:disabled{opacity:.5;cursor:not-allowed;}`;
const IconButton = styled.button`display:inline-flex;align-items:center;border:0;background:transparent;color:${p=>p.$danger?'#dc2626':'#0369a1'};cursor:pointer;padding:.15rem;&:hover:not(:disabled){color:${p=>p.$danger?'#b91c1c':'#0056b3'};}&:disabled{opacity:.5;cursor:not-allowed;}`;
const TalkHint = styled.div`font-size:.7rem;color:#94a3b8;margin-top:.4rem;`;
const TalkError = styled.div`font-size:.78rem;color:#b91c1c;margin-top:.4rem;`;
// The publish-consent answer is a real decision with consequences, so it's
// rendered as two selectable cards that spell out what each answer does to
// the slides, with the saved answer shown as a chip in the header. The PUT
// fires on click, so the chip doubles as save feedback ("Saving…" → "Answer
// saved") - the speaker never has to wonder whether the click stuck.
const ConsentBlock = styled.div`margin-top:.5rem;padding:.85rem;background:#fff;border:1px solid #e2e8f0;border-left:3px solid ${BLUE};`;
const ConsentHead = styled.div`display:flex;gap:.5rem;align-items:flex-start;margin-bottom:.65rem;`;
const ConsentIcon = styled.span`display:inline-flex;flex:none;align-items:center;justify-content:center;width:1.7rem;height:1.7rem;background:#eff9fe;color:${BLUE};`;
const ConsentQuestion = styled.p`flex:1;min-width:0;margin:0;font-size:.85rem;color:#111D4E;font-weight:600;line-height:1.35;`;
const ConsentState = styled.span`flex:none;font-size:.66rem;font-weight:800;text-transform:uppercase;letter-spacing:.03em;padding:.15rem .45rem;border-radius:999px;background:${p=>p.$tone==='granted'?'#dcfce7':p.$tone==='denied'?'#fee2e2':'#eef2f7'};color:${p=>p.$tone==='granted'?'#166534':p.$tone==='denied'?'#991b1b':'#475569'};`;
const ConsentOptions = styled.div`display:flex;flex-direction:column;gap:.4rem;`;
// Selected card: blue border on the light-blue tint already used for the
// admin's selected rows, so the answer is readable at a glance without
// relying on the (small) radio dot alone.
const ConsentOption = styled.label`
  display:flex;align-items:flex-start;gap:.55rem;padding:.55rem .65rem;border:1px solid #e2e8f0;background:#f8fafc;font-weight:400;cursor:pointer;transition:border-color .15s,background-color .15s;
  &:hover{border-color:#94a3b8;}
  ${p=>p.$selected&&`border-color:${BLUE};background:#eff9fe;`}
  ${p=>p.$disabled&&'cursor:not-allowed;opacity:.7;'}
  input{appearance:none;flex:none;width:1.05rem;height:1.05rem;margin:.12rem 0 0;border:2px solid #94a3b8;border-radius:50%;background:#fff;cursor:inherit;transition:border-width .12s;
    &:checked{border-color:${BLUE};border-width:5px;}
    &:focus-visible{outline:2px solid #4e4094;outline-offset:2px;}}
`;
const ConsentOptionText = styled.span`display:flex;flex-direction:column;gap:.1rem;min-width:0;`;
const ConsentOptionTitle = styled.span`font-size:.85rem;color:#111D4E;font-weight:600;line-height:1.3;`;
const ConsentOptionNote = styled.span`font-size:.74rem;color:#5B6B78;line-height:1.3;`;
const ConsentPrivacyHint = styled.div`font-size:.72rem;color:#94a3b8;margin-top:.5rem;`;

// The speaker's own state (Readiness) and "I present without slides" beside it.
const StateRow = styled.div`display:flex;flex-wrap:wrap;gap:.45rem .9rem;align-items:center;margin:.15rem 0 .4rem;`;
const StateLine = styled.div`font-size:.86rem;font-weight:700;color:${p=>p.$tone==='covered'?'#166534':p.$tone==='missing'?'#b91c1c':'#075985'};`;
const CheckLabel = styled.label`display:inline-flex;align-items:center;gap:.4rem;font-size:.8rem;color:#334155;input{flex:none;width:1rem;height:1rem;margin:0;accent-color:${BLUE};}`;

// "Presenting with": one co-speaker per line, wrapping on a phone.
const CoSpeakerList = styled.div`display:flex;flex-direction:column;gap:.3rem;`;
const CoSpeakerRow = styled.div`display:flex;flex-wrap:wrap;align-items:baseline;gap:.35rem;font-size:.84rem;color:#111D4E;`;
const CoSpeakerName = styled.span`font-weight:600;overflow-wrap:anywhere;`;
const CoSpeakerEmail = styled.a`color:#0369a1;overflow-wrap:anywhere;&:hover{text-decoration:underline;}`;
const CoSpeakerNote = styled.span`color:#5B6B78;font-size:.78rem;`;
const NotConfirmedTag = styled.span`display:inline-block;padding:.08rem .4rem;font-size:.66rem;font-weight:800;text-transform:uppercase;letter-spacing:.03em;background:#e0f2fe;color:#075985;`;

// The order: the banner is the most prominent thing in the card while it is needed, like an open
// invitation card (brand blue on its left edge).
const OrderBanner = styled.div`display:flex;flex-direction:column;gap:.45rem;align-items:flex-start;margin:.45rem 0 .55rem;padding:.75rem .8rem;background:#fff;border-left:4px solid ${BLUE};box-shadow:0 6px 16px rgba(15,23,42,.06);`;
const OrderBannerTitle = styled.div`font-weight:800;color:#111D4E;font-size:.9rem;`;
const OrderBannerWhy = styled.div`color:#5B6B78;font-size:.8rem;line-height:1.4;`;

// The files: one column, a big position badge first, nothing side by side that can grow.
const DeckList = styled.ol`list-style:none;margin:0;padding:0;`;
const DeckRow = styled.li`display:flex;gap:.65rem;align-items:flex-start;min-width:0;padding:.6rem 0;border-top:1px solid #eef2f7;&:first-child{border-top:0;}`;
const PositionBadge = styled.span`display:inline-flex;align-items:center;justify-content:center;flex:none;min-width:2.1rem;height:2.1rem;padding:0 .3rem;background:#111D4E;color:#fff;font-weight:800;font-size:1.05rem;line-height:1;`;
const DeckMain = styled.div`flex:1;min-width:0;display:flex;flex-direction:column;gap:.12rem;`;
const DeckNameRow = styled.div`display:flex;align-items:center;gap:.35rem;min-width:0;`;
const DeckMeta = styled.div`color:#5B6B78;font-size:.78rem;line-height:1.4;overflow-wrap:anywhere;`;
const DeckOrphan = styled(DeckMeta)`font-weight:600;color:#b45309;`;
// Who presents a file is what co-speakers decide together, so it reads stronger than the upload line.
const DeckPresenters = styled.div`font-size:.8rem;font-weight:700;color:#111D4E;line-height:1.4;overflow-wrap:anywhere;`;
const DeckActions = styled.div`display:flex;flex-wrap:wrap;gap:.4rem;align-items:center;margin-top:.3rem;`;
const InlineConfirm = styled.div`margin-top:.4rem;padding:.5rem .6rem;background:#fff;border:1px solid #e2e8f0;border-left:3px solid #dc2626;`;
const InlineConfirmTitle = styled.div`font-size:.82rem;font-weight:700;color:#111D4E;`;
const InlineConfirmBody = styled.div`font-size:.78rem;color:#5B6B78;line-height:1.4;margin:.1rem 0 .4rem;`;
const PresenterOptions = styled.div`display:flex;flex-direction:column;gap:.3rem;margin-bottom:.4rem;`;
const PresenterOption = styled.label`display:flex;align-items:center;gap:.5rem;padding:.35rem .5rem;border:1px solid #e2e8f0;background:#f8fafc;font-size:.85rem;color:#111D4E;cursor:pointer;&:hover{border-color:#94a3b8;}input{flex:none;width:1rem;height:1rem;margin:0;accent-color:${BLUE};}`;
const AddBlock = styled.div`margin-top:.55rem;padding:.75rem;background:#fff;border:1px solid #e2e8f0;`;
const AddFileName = styled.div`min-width:0;font-size:.82rem;color:#111D4E;margin-top:.35rem;overflow-wrap:anywhere;`;
const PositionSelect = styled.select`display:block;width:100%;max-width:100%;padding:.45rem .5rem;border:1px solid #cbd5e1;background:#fff;font:inherit;font-size:.85rem;`;

const messageOf = (err, fallback) => {
  const data = err?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data && typeof data.error === 'string' && data.error) return data.error;
  return fallback;
};
const isConflict = (err) => err?.response?.status === 409;

// Per-spot talk editor: title suggestion, the co-speakers, the consent question, the talk's files
// in their confirmed order, and "I present without slides". Every write answers the talk's row of
// GET /api/me/talks, which the parent swaps into the list; an order action answered with a 409
// means another speaker changed the files in between, so the parent reads the talks again.
// `defaultOpen`: a spot accepted a moment ago opens on its talk, the next thing to do.
const TalkPanel = ({ talk, viewerName, onUpdated, onReload, defaultOpen }) => {
  const intl = useIntl();
  const t = (id, def, values) => intl.formatMessage({ id, defaultMessage: def }, values);
  const [open, setOpen] = useState(Boolean(defaultOpen));
  // The entry title is the organizer's; the site language picks which of its two forms is shown
  // (English-first for an English visitor, as utils/eventName.js does for events).
  const entryTitle = (intl.locale || '').startsWith('en')
    ? (talk.titleEn || talk.titleDe || '') : (talk.titleDe || talk.titleEn || '');
  const [title, setTitle] = useState(talk.titleSuggestion || entryTitle || '');
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [titleError, setTitleError] = useState('');
  const [consentSaving, setConsentSaving] = useState(false);
  const [consentError, setConsentError] = useState('');
  // Answered blocks start folded; null = follow the data, true/false = the speaker's own click.
  const [consentOpen, setConsentOpen] = useState(null);
  const [sharedOpen, setSharedOpen] = useState(null);
  const [withoutError, setWithoutError] = useState('');
  const [withoutWarning, setWithoutWarning] = useState(false);
  const [orderError, setOrderError] = useState('');
  const [arranging, setArranging] = useState(false);
  const [draftOrder, setDraftOrder] = useState([]);
  const [removeConfirmId, setRemoveConfirmId] = useState(null);
  const [editingPresentersId, setEditingPresentersId] = useState(null);
  const [presenterDraft, setPresenterDraft] = useState([]);
  const [rowErrors, setRowErrors] = useState({});
  const [adding, setAdding] = useState(false);
  const [addingUpload, setAddingUpload] = useState(false);
  const [addFile, setAddFile] = useState(null);
  const [addPresenters, setAddPresenters] = useState([talk.assignmentId]);
  const [addPosition, setAddPosition] = useState('');
  const [addError, setAddError] = useState('');
  const addRef = useRef(null);
  const replaceRef = useRef(null);
  const replacingId = useRef(null);

  const currentTitle = talk.titleSuggestion || entryTitle || '';
  const changed = title.trim() !== currentTitle;
  const adminOwned = Boolean(talk.titleEn || talk.titleDe);
  const consent = talk.consent || 'UNSET';
  const consentAnswered = consent !== 'UNSET';
  const needsSlides = talk.asksForSlides !== false;
  const decks = [...(talk.decks || [])].sort((a, b) => a.position - b.position);
  const coSpeakers = talk.coSpeakers || [];
  const presentsFiles = decks.some((deck) => (deck.presenterAssignmentIds || []).includes(talk.assignmentId));
  const viewer = viewerName || t('speaker.talk.you', 'You');
  // The presenter choices: the viewer (a confirmed speaker, since this row is their own spot) and
  // the confirmed co-speakers. An unconfirmed co-speaker cannot present a file (the backend refuses).
  const presenterOptions = [
    { assignmentId: talk.assignmentId, name: viewer },
    ...coSpeakers.filter((c) => c.confirmed).map((c) => ({ assignmentId: c.assignmentId, name: c.name })),
  ];
  const presenterName = (assignmentId) => {
    const option = presenterOptions.find((o) => o.assignmentId === assignmentId);
    if (option) return option.name;
    const co = coSpeakers.find((c) => c.assignmentId === assignmentId);
    return co ? co.name : t('speaker.talk.anotherSpeaker', 'Another speaker');
  };
  const formatDate = (value) => (value
    ? new Date(value).toLocaleDateString(intl.locale, { year: 'numeric', month: 'short', day: 'numeric' })
    : '');
  // A deck without a current version (corrupt data, which data health reports) still gets a row.
  const fileNameOf = (deck) => deck.fileName || t('speaker.talk.noFileName', 'File without a version');
  const privacyPolicyUrl = `${API}/uploads/privacy_policy_${intl.locale === 'de' ? 'de' : 'en'}.pdf`;
  const hasContent = Boolean(entryTitle || decks.length);
  const orderedDecks = arranging
    ? draftOrder.map((id) => decks.find((deck) => deck.id === id)).filter(Boolean)
    : decks;
  const setRowError = (deckId, message) => setRowErrors((current) => ({ ...current, [deckId]: message }));
  const clearRowError = (deckId) => setRowErrors((current) => {
    if (!current[deckId]) return current;
    const next = { ...current };
    delete next[deckId];
    return next;
  });

  const saveTitle = async () => {
    setBusy(true); setTitleError('');
    try {
      const answer = await SelfApi.suggestTalkTitle(talk.entryId, title);
      onUpdated({ ...talk, titleSuggestion: answer.titleSuggestion });
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch (err) { setTitleError(messageOf(err, t('speaker.talk.saveError', 'Could not save the talk title.'))); }
    finally { setBusy(false); }
  };

  const setConsent = async (value) => {
    setConsentSaving(true); setBusy(true); setConsentError('');
    try {
      const answer = await SelfApi.setMaterialsConsent(talk.assignmentId, value);
      onUpdated({ ...talk, consent: answer.consent });
      setConsentOpen(false);
    } catch (err) { setConsentError(messageOf(err, t('speaker.talk.consentSaveError', 'Could not save your answer. Please try again.'))); }
    finally { setConsentSaving(false); setBusy(false); }
  };

  const downloadFile = async (deck) => {
    if (!deck.versionId) return;
    setBusy(true); clearRowError(deck.id);
    try { await SelfApi.downloadDeck(deck.versionId, deck.fileName); }
    catch (err) { setRowError(deck.id, messageOf(err, t('speaker.talk.downloadError', 'Could not download the file. Please try again.'))); }
    finally { setBusy(false); }
  };

  const pickReplacement = (deckId) => {
    replacingId.current = deckId;
    replaceRef.current?.click();
  };
  const onReplacementFile = async (event) => {
    const file = event.target.files?.[0];
    const deckId = replacingId.current;
    event.target.value = '';
    replacingId.current = null;
    if (!file || !deckId) return;
    setBusy(true); clearRowError(deckId);
    try { onUpdated(await SelfApi.addDeckVersion(talk.entryId, deckId, file)); }
    catch (err) { setRowError(deckId, messageOf(err, t('speaker.talk.uploadError', 'Upload failed. Please try again.'))); }
    finally { setBusy(false); }
  };

  const removeFile = async (deckId) => {
    setBusy(true); clearRowError(deckId);
    try {
      onUpdated(await SelfApi.removeDeck(talk.entryId, deckId));
      setRemoveConfirmId(null);
    } catch (err) { setRowError(deckId, messageOf(err, t('speaker.talk.removeError', 'Could not remove the file.'))); }
    finally { setBusy(false); }
  };

  const startPresenters = (deck) => {
    clearRowError(deck.id);
    setEditingPresentersId(deck.id);
    setPresenterDraft([...(deck.presenterAssignmentIds || [])]);
  };
  const togglePresenter = (assignmentId) => setPresenterDraft((current) => (
    current.includes(assignmentId) ? current.filter((id) => id !== assignmentId) : [...current, assignmentId]
  ));
  const savePresenters = async (deckId) => {
    if (!presenterDraft.length) return;
    setBusy(true); clearRowError(deckId);
    try {
      onUpdated(await SelfApi.setDeckPresenters(talk.entryId, deckId, presenterDraft));
      setEditingPresentersId(null);
    } catch (err) { setRowError(deckId, messageOf(err, t('speaker.talk.presentersError', 'Could not change who presents this file. Please try again.'))); }
    finally { setBusy(false); }
  };

  const startArrange = () => {
    setOrderError('');
    setDraftOrder(decks.map((deck) => deck.id));
    setArranging(true);
  };
  const moveDeck = (index, delta) => setDraftOrder((current) => {
    const target = index + delta;
    if (target < 0 || target >= current.length) return current;
    const next = [...current];
    [next[index], next[target]] = [next[target], next[index]];
    return next;
  });
  // A 409 means the files changed under this screen (another speaker added, removed or reordered
  // one): read the talks again and say that the state on screen is the current one.
  const reloadAfterConflict = async () => {
    setOrderError(t('speaker.talk.filesChanged', 'The files changed meanwhile. The current state is shown.'));
    setArranging(false);
    await onReload?.();
  };
  const saveOrder = async () => {
    setBusy(true); setOrderError('');
    try {
      onUpdated(await SelfApi.reorderDecks(talk.entryId, draftOrder));
      setArranging(false);
    } catch (err) {
      if (isConflict(err)) await reloadAfterConflict();
      else setOrderError(messageOf(err, t('speaker.talk.orderError', 'Could not save the order. Please try again.')));
    } finally { setBusy(false); }
  };
  const confirmOrder = async () => {
    setBusy(true); setOrderError('');
    try { onUpdated(await SelfApi.confirmDeckOrder(talk.entryId, decks.map((deck) => deck.id))); }
    catch (err) {
      if (isConflict(err)) await reloadAfterConflict();
      else setOrderError(messageOf(err, t('speaker.talk.orderConfirmError', 'Could not confirm the order. Please try again.')));
    } finally { setBusy(false); }
  };

  const saveWithoutSlides = async (value) => {
    setBusy(true); setWithoutError('');
    try {
      onUpdated(await SelfApi.setPresentsWithoutSlides(talk.assignmentId, value));
      setWithoutWarning(false);
    } catch (err) { setWithoutError(messageOf(err, t('speaker.talk.withoutSlidesError', 'Could not save your answer. Please try again.'))); }
    finally { setBusy(false); }
  };
  const toggleWithoutSlides = (checked) => {
    if (checked && presentsFiles) { setWithoutWarning(true); return; }
    saveWithoutSlides(checked);
  };

  const openAdd = () => {
    setAdding(true); setAddError('');
    setAddFile(null);
    setAddPresenters([talk.assignmentId]);
    setAddPosition('');
  };
  const closeAdd = () => {
    setAdding(false); setAddError('');
    setAddFile(null);
    setAddPresenters([talk.assignmentId]);
    setAddPosition('');
  };
  const onAddFileChosen = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    setAddFile(file || null);
  };
  const toggleAddPresenter = (assignmentId) => setAddPresenters((current) => (
    current.includes(assignmentId) ? current.filter((id) => id !== assignmentId) : [...current, assignmentId]
  ));
  // Open spots in the order (1-based insert positions) from the current files: nothing is
  // preselected, because the backend refuses an upload without a position once files exist.
  const positionOptions = decks.length === 0 ? [] : [
    { value: 1, label: t('speaker.talk.positionBefore', 'Before {position}. {file}', { position: 1, file: fileNameOf(decks[0]) }) },
    ...decks.slice(0, -1).map((deck, index) => ({
      value: index + 2,
      label: t('speaker.talk.positionBetween', 'Between {before} and {after}', { before: index + 1, after: index + 2 }),
    })),
    { value: decks.length + 1, label: t('speaker.talk.positionAfter', 'After {position}. {file}', { position: decks.length, file: fileNameOf(decks[decks.length - 1]) }) },
  ];
  const canAdd = Boolean(addFile) && addPresenters.length > 0 && (decks.length === 0 || addPosition !== '');
  const submitAdd = async () => {
    if (!canAdd) return;
    setBusy(true); setAddingUpload(true); setAddError('');
    try {
      onUpdated(await SelfApi.addDeck(talk.entryId, addFile, {
        position: decks.length ? Number(addPosition) : null,
        presenterAssignmentIds: addPresenters,
      }));
      closeAdd();
    } catch (err) { setAddError(messageOf(err, t('speaker.talk.addError', 'Could not add the file. Please try again.'))); }
    finally { setAddingUpload(false); setBusy(false); }
  };

  const stateTone = talk.readiness?.me === 'COVERED' ? 'covered'
    : talk.readiness?.me === 'WITHOUT_SLIDES' ? 'without' : 'missing';
  const stateText = talk.readiness?.me === 'COVERED'
    ? t('speaker.talk.stateCovered', 'Your part is covered')
    : talk.readiness?.me === 'WITHOUT_SLIDES'
      ? t('speaker.talk.stateWithoutSlides', 'You present without slides')
      : t('speaker.talk.stateMissing', 'Your slides are still missing');
  const orderConfirmed = decks.length >= 2 && talk.order?.confirmedAt && talk.order?.confirmedByName;
  const needsConfirmation = decks.length >= 2 && Boolean(talk.order?.needsConfirmation);
  // A shared talk: the files belong to every speaker of the talk, who settle together who presents
  // which file and the order they are shown in.
  const shared = needsSlides && coSpeakers.length > 0;
  const coNames = coSpeakers.map((c) => c.name).join(', ');
  const orphanIds = decks.filter((deck) => (deck.presenterAssignmentIds || []).length === 0
    || (talk.readiness?.orphanDeckIds || []).includes(deck.id)).map((deck) => deck.id);
  const missingIds = talk.readiness?.missingAssignmentIds || [];
  const presentersSettled = decks.length > 0 && orphanIds.length === 0 && missingIds.length === 0;
  const presentersDetail = decks.length === 0
    ? t('speaker.talk.sharedNoFilesYet', 'No files uploaded yet.')
    : orphanIds.length > 0
      ? t('speaker.talk.sharedOrphans', '{count, plural, one {# file has} other {# files have}} nobody presenting it yet.', { count: orphanIds.length })
      : missingIds.length > 0
        ? t('speaker.talk.sharedMissing', 'Not presenting anything yet: {names}', { names: missingIds.map(presenterName).join(', ') })
        : t('speaker.talk.sharedPresentersDone', 'Every file has a presenter.');
  const orderSettled = decks.length >= 2 && !needsConfirmation;
  // Everything the co-speakers settle together is done: the box folds to one line.
  const sharedSettled = presentersSettled && (decks.length < 2 || orderSettled);
  const showShared = sharedOpen ?? !sharedSettled;
  const showConsent = !consentAnswered || (consentOpen ?? false);
  const orderDetail = decks.length < 2
    ? t('speaker.talk.sharedOrderLater', 'Needed as soon as there is more than one file.')
    : needsConfirmation
      ? t('speaker.talk.sharedOrderOpen', 'Not confirmed yet - one of you confirms it for everyone.')
      : orderConfirmed
        ? t('speaker.talk.orderConfirmed', 'Order confirmed by {name} on {date}.', { name: talk.order.confirmedByName, date: formatDate(talk.order.confirmedAt) })
        : t('speaker.talk.sharedOrderDone', 'Order confirmed.');

  return (
    <TalkArea>
      <ToggleRow type="button" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <ToggleLeft>
          <ToggleTitle>
            {hasContent
              ? <FormattedMessage id="speaker.talk.header" defaultMessage="Your talk" />
              : needsSlides
                ? <FormattedMessage id="speaker.talk.add" defaultMessage="Add your talk & slides" />
                : <FormattedMessage id="speaker.talk.addTitleOnly" defaultMessage="Add your talk title" />}
          </ToggleTitle>
          {!open && hasContent && (
            <ToggleSummary>
              {entryTitle && <SummaryTitle>{entryTitle}</SummaryTitle>}
              {decks.length > 0 && (
                <SummarySlides>
                  <FileText size={12} />
                  <SummaryFileName>
                    {decks.length === 1
                      ? fileNameOf(decks[0])
                      : t('speaker.talk.fileCount', '{count, plural, one {# file} other {# files}}', { count: decks.length })}
                  </SummaryFileName>
                </SummarySlides>
              )}
            </ToggleSummary>
          )}
          {shared && (
            <SummaryShared>
              <Users size={12} />
              {t('speaker.talk.sharedWith', 'Shared talk with {names}', { names: coNames })}
            </SummaryShared>
          )}
          {!open && needsSlides && (needsConfirmation || stateTone === 'missing' || (shared && decks.length > 0 && !presentersSettled)) && (
            <SummaryAttention>
              {needsConfirmation
                ? <FormattedMessage id="speaker.talk.orderBannerTitle" defaultMessage="Please confirm the order of your files" />
                : stateTone === 'missing'
                  ? <FormattedMessage id="speaker.talk.stateMissing" defaultMessage="Your slides are still missing" />
                  : <FormattedMessage id="speaker.talk.sharedPresentersAttention" defaultMessage="Please decide who presents which file" />}
            </SummaryAttention>
          )}
        </ToggleLeft>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </ToggleRow>
      {open && (
      <TalkBody>
      {shared && (
        <SharedBox data-testid="shared-talk-box">
          {sharedSettled ? (
            <FoldRow type="button" aria-expanded={showShared} onClick={() => setSharedOpen(!showShared)}>
              <Users size={16} />
              <FoldTitle>
                <FormattedMessage id="speaker.talk.sharedTitle" defaultMessage="You share this talk" />
                <ConsentState $tone="granted"><FormattedMessage id="speaker.talk.sharedAllSettled" defaultMessage="All agreed" /></ConsentState>
              </FoldTitle>
              <FoldAction>
                {showShared
                  ? <><FormattedMessage id="speaker.talk.fold" defaultMessage="Hide" /><ChevronUp size={14} /></>
                  : <><FormattedMessage id="speaker.talk.unfold" defaultMessage="Show" /><ChevronDown size={14} /></>}
              </FoldAction>
            </FoldRow>
          ) : (
            <SharedHead><Users size={16} /><FormattedMessage id="speaker.talk.sharedTitle" defaultMessage="You share this talk" /></SharedHead>
          )}
          {showShared && (<>
          <SharedText>
            <FormattedMessage id="speaker.talk.sharedText" defaultMessage="The presentation files of this talk belong to all of its speakers: each of you can add, replace and reorder them. Please agree with each other and settle these two points:" />
          </SharedText>
          <TodoList>
            <TodoItem $done={presentersSettled}>
              {presentersSettled ? <Check size={15} /> : <Circle size={15} />}
              <TodoText>
                <TodoTitle><FormattedMessage id="speaker.talk.sharedPresentersTodo" defaultMessage="Who presents which file" /></TodoTitle>
                <TodoDetail>{presentersDetail}</TodoDetail>
              </TodoText>
            </TodoItem>
            <TodoItem $done={orderSettled} $idle={decks.length < 2}>
              {orderSettled ? <Check size={15} /> : <Circle size={15} />}
              <TodoText>
                <TodoTitle><FormattedMessage id="speaker.talk.sharedOrderTodo" defaultMessage="The order the files are shown in" /></TodoTitle>
                <TodoDetail>{orderDetail}</TodoDetail>
              </TodoText>
            </TodoItem>
          </TodoList>
          <FieldLabel><FormattedMessage id="speaker.talk.presentingWith" defaultMessage="Presenting with" /></FieldLabel>
          <CoSpeakerList>
            {coSpeakers.map((coSpeaker) => (
              <CoSpeakerRow key={coSpeaker.assignmentId}>
                <CoSpeakerName>{coSpeaker.name}</CoSpeakerName>
                {coSpeaker.email
                  ? <CoSpeakerEmail href={`mailto:${coSpeaker.email}`} title={t('speaker.talk.emailTitle', 'Write to {name}', { name: coSpeaker.name })}>{coSpeaker.email}</CoSpeakerEmail>
                  : <CoSpeakerNote><FormattedMessage id="speaker.talk.noEmail" defaultMessage="no email on file" /></CoSpeakerNote>}
                {!coSpeaker.confirmed && (
                  <NotConfirmedTag><FormattedMessage id="speaker.talk.notConfirmed" defaultMessage="not confirmed yet" /></NotConfirmedTag>
                )}
              </CoSpeakerRow>
            ))}
          </CoSpeakerList>
          </>)}
        </SharedBox>
      )}
      {needsSlides && (
        <>
          <StateRow>
            <StateLine $tone={stateTone}>{stateText}</StateLine>
            <CheckLabel>
              <input
                type="checkbox"
                checked={Boolean(talk.presentsWithoutSlides)}
                disabled={busy}
                onChange={(e) => toggleWithoutSlides(e.target.checked)}
              />
              <FormattedMessage id="speaker.talk.withoutSlides" defaultMessage="I present without slides" />
            </CheckLabel>
          </StateRow>
          {withoutWarning && (
            <InlineConfirm role="alert">
              <InlineConfirmBody>
                <FormattedMessage id="speaker.talk.withoutSlidesWarning" defaultMessage="This removes you from the files you present." />
              </InlineConfirmBody>
              <SlideBtnRow>
                <MiniButton disabled={busy} onClick={() => saveWithoutSlides(true)}>
                  <FormattedMessage id="speaker.talk.withoutSlidesConfirm" defaultMessage="Yes, I present without slides" />
                </MiniButton>
                <MiniButton $ghost disabled={busy} onClick={() => setWithoutWarning(false)}>
                  <FormattedMessage id="speaker.talk.cancel" defaultMessage="Cancel" />
                </MiniButton>
              </SlideBtnRow>
            </InlineConfirm>
          )}
          {withoutError && <TalkError>{withoutError}</TalkError>}
        </>
      )}

      <FieldLabel>{adminOwned
        ? <FormattedMessage id="speaker.talk.titleAdminLabel" defaultMessage="Talk title (set by organizer)" />
        : <FormattedMessage id="speaker.talk.titleLabel" defaultMessage="Talk title" />}</FieldLabel>
      <TitleRow>
        <TalkInput value={title} onChange={e => setTitle(e.target.value)} placeholder={t('speaker.talk.titlePlaceholder', 'e.g. The Future of Valve Repair')} />
        <MiniButton onClick={saveTitle} disabled={busy || !changed}>
          {saved ? <><Check size={14} /><FormattedMessage id="speaker.talk.saved" defaultMessage="Saved" /></>
                 : adminOwned
                   ? (talk.titleSuggestion
                       ? <FormattedMessage id="speaker.talk.suggestPending" defaultMessage="Change requested" />
                       : <FormattedMessage id="speaker.talk.suggest" defaultMessage="Suggest change" />)
                   : <FormattedMessage id="speaker.talk.save" defaultMessage="Save" />}
        </MiniButton>
      </TitleRow>
      {adminOwned && (
        <TalkHint>
          {talk.titleSuggestion
            ? <FormattedMessage id="speaker.talk.pending" defaultMessage="Title change request pending review" />
            : <FormattedMessage id="speaker.talk.suggestionHint" defaultMessage="The organizer will review your suggestion." />}
        </TalkHint>
      )}
      {titleError && <TalkError>{titleError}</TalkError>}

      {coSpeakers.length > 0 && !shared && (
        <>
          <FieldLabel><FormattedMessage id="speaker.talk.presentingWith" defaultMessage="Presenting with" /></FieldLabel>
          <CoSpeakerList>
            {coSpeakers.map((coSpeaker) => (
              <CoSpeakerRow key={coSpeaker.assignmentId}>
                <CoSpeakerName>{coSpeaker.name}</CoSpeakerName>
                {coSpeaker.email
                  ? <CoSpeakerEmail href={`mailto:${coSpeaker.email}`} title={t('speaker.talk.emailTitle', 'Write to {name}', { name: coSpeaker.name })}>{coSpeaker.email}</CoSpeakerEmail>
                  : <CoSpeakerNote><FormattedMessage id="speaker.talk.noEmail" defaultMessage="no email on file" /></CoSpeakerNote>}
                {!coSpeaker.confirmed && (
                  <NotConfirmedTag><FormattedMessage id="speaker.talk.notConfirmed" defaultMessage="not confirmed yet" /></NotConfirmedTag>
                )}
              </CoSpeakerRow>
            ))}
          </CoSpeakerList>
        </>
      )}

      {!needsSlides && (
        <TalkHint><FormattedMessage id="speaker.talk.slidesNotRequired" defaultMessage="No presentation is required for this engagement." /></TalkHint>
      )}

      {needsSlides && (
        <>
          <ConsentBlock>
            {consentAnswered && !consentSaving && (
              <FoldRow type="button" aria-expanded={showConsent} onClick={() => setConsentOpen(!showConsent)} data-testid="consent-fold">
                <ConsentIcon><ShieldCheck size={16} /></ConsentIcon>
                <FoldTitle>
                  <FormattedMessage id="speaker.talk.consentFoldTitle" defaultMessage="Publishing your materials" />
                  <ConsentState $tone={consent === 'GRANTED' ? 'granted' : 'denied'}>
                    {consent === 'GRANTED'
                      ? <FormattedMessage id="speaker.talk.consentFoldYes" defaultMessage="Yes, may be published" />
                      : <FormattedMessage id="speaker.talk.consentFoldNo" defaultMessage="No, internal only" />}
                  </ConsentState>
                </FoldTitle>
                <FoldAction>
                  {showConsent
                    ? <><FormattedMessage id="speaker.talk.fold" defaultMessage="Hide" /><ChevronUp size={14} /></>
                    : <><FormattedMessage id="speaker.talk.consentChange" defaultMessage="Change" /><ChevronDown size={14} /></>}
                </FoldAction>
              </FoldRow>
            )}
            {(showConsent || consentSaving) && (<>
            <ConsentHead style={consentAnswered && !consentSaving ? { marginTop: '.65rem' } : undefined}>
              {/* Once answered, the fold row above carries the icon and the answer. */}
              {(!consentAnswered || consentSaving) && <ConsentIcon><ShieldCheck size={16} /></ConsentIcon>}
              <ConsentQuestion><FormattedMessage id="speaker.talk.consentQuestion" defaultMessage='May we publish your presentation materials (e.g. on the HEARTS website) in support of "Democratizing Science"?' /></ConsentQuestion>
              {consentSaving && <ConsentState><FormattedMessage id="speaker.talk.consentSaving" defaultMessage="Saving…" /></ConsentState>}
            </ConsentHead>
            <ConsentOptions>
              <ConsentOption $selected={consent === 'GRANTED'} $disabled={busy}>
                <input type="radio" name={`materialsConsent-${talk.assignmentId}`} checked={consent === 'GRANTED'} disabled={busy} onChange={() => setConsent('GRANTED')} />
                <ConsentOptionText>
                  <ConsentOptionTitle><FormattedMessage id="speaker.talk.consentYes" defaultMessage="Yes, you may publish my presentation materials" /></ConsentOptionTitle>
                  <ConsentOptionNote><FormattedMessage id="speaker.talk.consentYesNote" defaultMessage="Your slides may be shown publicly on the HEARTS website." /></ConsentOptionNote>
                </ConsentOptionText>
              </ConsentOption>
              <ConsentOption $selected={consent === 'DENIED'} $disabled={busy}>
                <input type="radio" name={`materialsConsent-${talk.assignmentId}`} checked={consent === 'DENIED'} disabled={busy} onChange={() => setConsent('DENIED')} />
                <ConsentOptionText>
                  <ConsentOptionTitle><FormattedMessage id="speaker.talk.consentNo" defaultMessage="No, please keep my presentation materials internal only" /></ConsentOptionTitle>
                  <ConsentOptionNote><FormattedMessage id="speaker.talk.consentNoNote" defaultMessage="Your slides will not be published - we use them internally only." /></ConsentOptionNote>
                </ConsentOptionText>
              </ConsentOption>
            </ConsentOptions>
            <ConsentPrivacyHint>
              <FormattedMessage id="speaker.talk.consentPrivacyHint" defaultMessage="You can change your answer at any time. See our {privacyPolicy} for details."
                values={{ privacyPolicy: <a href={privacyPolicyUrl} target="_blank" rel="noreferrer"><FormattedMessage id="footer.privacyPolicy" defaultMessage="Privacy Policy" /></a> }} />
            </ConsentPrivacyHint>
            {!consentAnswered && (
              <TalkHint><FormattedMessage id="speaker.talk.consentRequiredHint" defaultMessage="Please choose an option above before uploading your slides." /></TalkHint>
            )}
            </>)}
            {consentError && <TalkError>{consentError}</TalkError>}
          </ConsentBlock>

          <FieldLabel>{shared
            ? <FormattedMessage id="speaker.talk.filesLabelShared" defaultMessage="Shared presentation files" />
            : <FormattedMessage id="speaker.talk.filesLabel" defaultMessage="Presentation files" />}</FieldLabel>
          {needsConfirmation && !arranging && (
            <OrderBanner role="status">
              <OrderBannerTitle><FormattedMessage id="speaker.talk.orderBannerTitle" defaultMessage="Please confirm the order of your files" /></OrderBannerTitle>
              <OrderBannerWhy>{shared
                ? t('speaker.talk.orderBannerWhyShared', 'The event team shows them in this order. Please agree on it with {names} - one confirmation counts for all of you.', { names: coNames })
                : <FormattedMessage id="speaker.talk.orderBannerWhy" defaultMessage="The event team shows them in this order." />}</OrderBannerWhy>
              <MiniButton disabled={busy} onClick={confirmOrder}>
                <Check size={14} /><FormattedMessage id="speaker.talk.orderConfirm" defaultMessage="This is the order we present in" />
              </MiniButton>
            </OrderBanner>
          )}
          {decks.length === 0 ? (
            <TalkHint><FormattedMessage id="speaker.talk.noFiles" defaultMessage="No presentation files yet." /></TalkHint>
          ) : (
            <DeckList>
              {orderedDecks.map((deck, index) => {
                const presenterIds = deck.presenterAssignmentIds || [];
                const isOrphan = Boolean(talk.readiness?.orphanDeckIds?.includes(deck.id)) || presenterIds.length === 0;
                const editing = editingPresentersId === deck.id;
                return (
                  <DeckRow key={deck.id} data-testid="deck-row">
                    <PositionBadge data-testid="deck-position">{index + 1}</PositionBadge>
                    <DeckMain>
                      <DeckNameRow>
                        <SlideName
                          type="button"
                          title={fileNameOf(deck)}
                          disabled={busy || arranging || !deck.versionId}
                          onClick={() => downloadFile(deck)}
                        >
                          {fileNameOf(deck)}
                        </SlideName>
                        {!arranging && (
                          <IconButton
                            type="button"
                            title={t('speaker.talk.download', 'Download')}
                            aria-label={t('speaker.talk.download', 'Download')}
                            disabled={busy || !deck.versionId}
                            onClick={() => downloadFile(deck)}
                          ><Download size={15} /></IconButton>
                        )}
                      </DeckNameRow>
                      {isOrphan ? (
                        <DeckOrphan><FormattedMessage id="speaker.talk.nobodyPresents" defaultMessage="Nobody presents this file yet" /></DeckOrphan>
                      ) : (
                        <DeckPresenters>{t('speaker.talk.presentedBy', 'Presented by {names}', { names: presenterIds.map(presenterName).join(', ') })}</DeckPresenters>
                      )}
                      <DeckMeta>
                        {deck.uploadedByName
                          ? t('speaker.talk.uploadedBy', 'Uploaded by {name} on {date}', { name: deck.uploadedByName, date: formatDate(deck.uploadedAt) })
                          : t('speaker.talk.uploadedOn', 'Uploaded on {date}', { date: formatDate(deck.uploadedAt) })}
                      </DeckMeta>
                      {!arranging && (
                        <DeckActions>
                          <MiniButton $ghost disabled={busy || !consentAnswered} onClick={() => pickReplacement(deck.id)}>
                            <Upload size={14} /><FormattedMessage id="speaker.talk.replace" defaultMessage="Replace" />
                          </MiniButton>
                          <MiniButton $ghost disabled={busy} onClick={() => (editing ? setEditingPresentersId(null) : startPresenters(deck))}>
                            <FormattedMessage id="speaker.talk.whoPresents" defaultMessage="Who presents" />
                          </MiniButton>
                          <IconButton
                            type="button"
                            $danger
                            title={t('speaker.talk.remove', 'Remove')}
                            aria-label={t('speaker.talk.remove', 'Remove')}
                            disabled={busy}
                            onClick={() => setRemoveConfirmId(deck.id)}
                          ><Trash2 size={15} /></IconButton>
                        </DeckActions>
                      )}
                      {arranging && (
                        <DeckActions>
                          <MiniButton $ghost disabled={busy || index === 0} onClick={() => moveDeck(index, -1)} title={t('speaker.talk.moveUp', 'Move up')}>
                            <ChevronUp size={14} /><FormattedMessage id="speaker.talk.moveUp" defaultMessage="Move up" />
                          </MiniButton>
                          <MiniButton $ghost disabled={busy || index === orderedDecks.length - 1} onClick={() => moveDeck(index, 1)} title={t('speaker.talk.moveDown', 'Move down')}>
                            <ChevronDown size={14} /><FormattedMessage id="speaker.talk.moveDown" defaultMessage="Move down" />
                          </MiniButton>
                        </DeckActions>
                      )}
                      {editing && !arranging && (
                        <InlineConfirm>
                          <FieldLabel><FormattedMessage id="speaker.talk.presentersLabel" defaultMessage="Who presents this file" /></FieldLabel>
                          <PresenterOptions>
                            {presenterOptions.map((option) => (
                              <PresenterOption key={option.assignmentId}>
                                <input
                                  type="checkbox"
                                  checked={presenterDraft.includes(option.assignmentId)}
                                  disabled={busy}
                                  onChange={() => togglePresenter(option.assignmentId)}
                                />
                                <span>{option.name}</span>
                              </PresenterOption>
                            ))}
                          </PresenterOptions>
                          {presenterDraft.length === 0 && (
                            <TalkError><FormattedMessage id="speaker.talk.presentersRequired" defaultMessage="Choose at least one person." /></TalkError>
                          )}
                          <SlideBtnRow>
                            <MiniButton disabled={busy || presenterDraft.length === 0} onClick={() => savePresenters(deck.id)}>
                              <FormattedMessage id="speaker.talk.save" defaultMessage="Save" />
                            </MiniButton>
                            <MiniButton $ghost disabled={busy} onClick={() => setEditingPresentersId(null)}>
                              <FormattedMessage id="speaker.talk.cancel" defaultMessage="Cancel" />
                            </MiniButton>
                          </SlideBtnRow>
                        </InlineConfirm>
                      )}
                      {removeConfirmId === deck.id && !arranging && (
                        <InlineConfirm role="alert">
                          <InlineConfirmTitle><FormattedMessage id="speaker.talk.removeConfirmTitle" defaultMessage="Remove this file?" /></InlineConfirmTitle>
                          <InlineConfirmBody><FormattedMessage id="speaker.talk.removeConfirmBody" defaultMessage="All versions of this file are deleted." /></InlineConfirmBody>
                          <SlideBtnRow>
                            <MiniButton $danger disabled={busy} onClick={() => removeFile(deck.id)}>
                              <Trash2 size={14} /><FormattedMessage id="speaker.talk.removeConfirm" defaultMessage="Remove file" />
                            </MiniButton>
                            <MiniButton $ghost disabled={busy} onClick={() => setRemoveConfirmId(null)}>
                              <FormattedMessage id="speaker.talk.cancel" defaultMessage="Cancel" />
                            </MiniButton>
                          </SlideBtnRow>
                        </InlineConfirm>
                      )}
                      {rowErrors[deck.id] && <TalkError>{rowErrors[deck.id]}</TalkError>}
                    </DeckMain>
                  </DeckRow>
                );
              })}
            </DeckList>
          )}

          {decks.length >= 2 && (
            <SlideBtnRow style={{ marginTop: '.35rem' }}>
              {arranging ? (
                <>
                  <MiniButton disabled={busy} onClick={saveOrder}>
                    <Check size={14} /><FormattedMessage id="speaker.talk.saveOrder" defaultMessage="Save order" />
                  </MiniButton>
                  <MiniButton $ghost disabled={busy} onClick={() => setArranging(false)}>
                    <FormattedMessage id="speaker.talk.cancel" defaultMessage="Cancel" />
                  </MiniButton>
                </>
              ) : (
                <MiniButton $ghost disabled={busy} onClick={startArrange}>
                  <FormattedMessage id="speaker.talk.changeOrder" defaultMessage="Change order" />
                </MiniButton>
              )}
            </SlideBtnRow>
          )}
          {orderError && <TalkError>{orderError}</TalkError>}
          {orderConfirmed && !needsConfirmation && (
            <TalkHint>
              <FormattedMessage id="speaker.talk.orderConfirmed" defaultMessage="Order confirmed by {name} on {date}."
                values={{ name: talk.order.confirmedByName, date: formatDate(talk.order.confirmedAt) }} />
            </TalkHint>
          )}

          <input ref={replaceRef} type="file" accept=".pptx,.ppt" style={{ display: 'none' }} onChange={onReplacementFile} data-testid="replace-file-input" />
          <input ref={addRef} type="file" accept=".pptx,.ppt" style={{ display: 'none' }} onChange={onAddFileChosen} data-testid="add-file-input" />

          <FieldLabel><FormattedMessage id="speaker.talk.addFile" defaultMessage="Add a file" /></FieldLabel>
          {!adding ? (
            <SlideBtnRow>
              <MiniButton $ghost disabled={busy || !consentAnswered || arranging} onClick={openAdd}>
                <Upload size={14} /><FormattedMessage id="speaker.talk.addFile" defaultMessage="Add a file" />
              </MiniButton>
            </SlideBtnRow>
          ) : (
            <AddBlock>
              <TalkHint><FormattedMessage id="speaker.talk.addRule" defaultMessage="If there is more than one file, they are shown in the order you present them - please put them in order and confirm it." /></TalkHint>
              <SlideBtnRow style={{ marginTop: '.5rem' }}>
                <MiniButton $ghost disabled={busy || !consentAnswered || arranging} onClick={() => addRef.current?.click()}>
                  <Upload size={14} /><FormattedMessage id="speaker.talk.chooseFile" defaultMessage="Choose file" />
                </MiniButton>
                {addFile && <AddFileName>{addFile.name}</AddFileName>}
              </SlideBtnRow>
              <FieldLabel><FormattedMessage id="speaker.talk.presentersLabel" defaultMessage="Who presents this file" /></FieldLabel>
              <PresenterOptions>
                {presenterOptions.map((option) => (
                  <PresenterOption key={option.assignmentId}>
                    <input
                      type="checkbox"
                      checked={addPresenters.includes(option.assignmentId)}
                      disabled={busy}
                      onChange={() => toggleAddPresenter(option.assignmentId)}
                    />
                    <span>{option.name}</span>
                  </PresenterOption>
                ))}
              </PresenterOptions>
              {addPresenters.length === 0 && (
                <TalkError><FormattedMessage id="speaker.talk.presentersRequired" defaultMessage="Choose at least one person." /></TalkError>
              )}
              {decks.length > 0 && (
                <>
                  <FieldLabel htmlFor={`talkPosition-${talk.entryId}`}><FormattedMessage id="speaker.talk.positionLabel" defaultMessage="Where does it go?" /></FieldLabel>
                  <PositionSelect
                    id={`talkPosition-${talk.entryId}`}
                    value={addPosition}
                    disabled={busy}
                    onChange={(e) => setAddPosition(e.target.value)}
                  >
                    <option value="">{t('speaker.talk.positionChoose', 'Choose where this file goes…')}</option>
                    {positionOptions.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </PositionSelect>
                </>
              )}
              {addError && <TalkError>{addError}</TalkError>}
              <SlideBtnRow style={{ marginTop: '.55rem' }}>
                <MiniButton disabled={busy || arranging || !canAdd} onClick={submitAdd}>
                  {addingUpload ? t('speaker.talk.uploading', 'Uploading…') : <FormattedMessage id="speaker.talk.addSubmit" defaultMessage="Add file" />}
                </MiniButton>
                <MiniButton $ghost disabled={busy} onClick={closeAdd}>
                  <FormattedMessage id="speaker.talk.cancel" defaultMessage="Cancel" />
                </MiniButton>
              </SlideBtnRow>
              <TalkHint><FormattedMessage id="speaker.talk.formats" defaultMessage="PowerPoint (.ppt or .pptx) · up to 100 MB" /></TalkHint>
            </AddBlock>
          )}
        </>
      )}
      </TalkBody>
      )}
    </TalkArea>
  );
};

export default TalkPanel;
